import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { Navigate, useSearchParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { GeneralSettings, LocalizationSettings, DataLayerSettings, DomainSettings } from '@/components/features/settings/components'
import { PageHeader } from '@/components/atoms'
import { settingsTabs } from '@/config/sidebar'
import { Spinner } from '@/components/ui/spinner'

/**
 * App settings. The tab comes from `?tab=` so each tab is its own item in the settings nav (§6.10); the tab bar is
 * only shown below `lg`, where that nav sits in the menu sheet.
 */
export const Settings = () => {
  const { businessProfile, isLoading, refreshFeatureFlags } = useFeatureFlags()
  const [params, setParams] = useSearchParams()
  const tabs = settingsTabs
  const tab = tabs.find((t) => t.value === params.get('tab')) ?? tabs[0]

  const selectTab = (value: string) => {
    setParams(value === settingsTabs[0].value ? {} : { tab: value }, { replace: true })
  }

  // The POS tab moved to Point of sale → Settings; old links still land there.
  if (params.get('tab') === 'pos') return <Navigate to="/pos/settings" replace />

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="size-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading settings…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="pb-10">
      <PageHeader size="large" title={tab.label} />

      <Tabs value={tab.value} onValueChange={selectTab} className="space-y-4">
        <div className="no-scrollbar overflow-x-auto lg:hidden">
          <TabsList>
            {tabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="general" className="mt-0 focus-visible:outline-none">
          <GeneralSettings profile={businessProfile} onRefresh={refreshFeatureFlags} />
        </TabsContent>

        <TabsContent value="localization" className="mt-0 focus-visible:outline-none">
          <LocalizationSettings businessProfile={businessProfile} onRefresh={refreshFeatureFlags} />
        </TabsContent>

        <TabsContent value="datalayer" className="mt-0 focus-visible:outline-none">
          <DataLayerSettings businessProfile={businessProfile} onRefresh={refreshFeatureFlags} />
        </TabsContent>

        <TabsContent value="domains" className="mt-0 focus-visible:outline-none">
          <DomainSettings businessProfile={businessProfile} onRefresh={refreshFeatureFlags} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
